/**
 * DataMigration - Populate UnifiedDataManager with existing data
 * date_of_change: 2025-08-29
 * 
 * This migration script ensures the UnifiedDataManager has access to existing
 * preloaded monsters and characters by converting them to the unified schema format.
 * Run this once to populate the system with default data.
 */

import UnifiedDataManager from './UnifiedDataManager';
import { CharacterDataService } from './CharacterDataService';
import { buildMonsterEntityFromRaw } from '../normalizers/MonsterEntityAdapter';
import { DEBUG_ENABLED } from '../utils/DebugLogger';
import { migrateDefaultCreatureData } from '../../../shared/rules/defaultCreatureMigration';
import NotificationManager from '../utils/NotificationManager';

// Import all monster JSON files statically to avoid fetch/import issues
import goblinData from '../data/monsters/goblin.json';
import skeletonData from '../data/monsters/skeleton.json';
import zombieData from '../data/monsters/zombie.json';
import wolfData from '../data/monsters/wolf.json';
import direWolfData from '../data/monsters/dire-wolf.json';
import brownBearData from '../data/monsters/brown-bear.json';
import bugbearData from '../data/monsters/bugbear.json';
import hobgoblinData from '../data/monsters/hobgoblin.json';
import orcData from '../data/monsters/orc.json';
import owlbearData from '../data/monsters/owlbear.json';

class DataMigration {
  static debugLog(...args) {
    if (DEBUG_ENABLED) {
      console.debug(...args);
    }
  }
  /**
   * Migrate preloaded monsters from existing JSON files to UnifiedDataManager
   * date_of_change: 2025-08-29
   */
  static async migratePreloadedMonsters() {
    this.debugLog('[DataMigration] Starting monster migration...');
    
    try {
      this.debugLog('[DataMigration] Checking for existing monsters...');
      const existingMonsters = UnifiedDataManager.getAllFromLocalStorage('monster');

      // Use statically imported monster data
      const monsterData = {
        'goblin': goblinData,
        'skeleton': skeletonData,
        'zombie': zombieData,
        'wolf': wolfData,
        'dire-wolf': direWolfData,
        'brown-bear': brownBearData,
        'bugbear': bugbearData,
        'hobgoblin': hobgoblinData,
        'orc': orcData,
        'owlbear': owlbearData
      };

      const migratedMonsters = { ...existingMonsters };
      const monsterIdsToReconcile = Object.keys(monsterData).filter((id) => {
        const existing = existingMonsters[id];
        if (!existing) return true;

        // Built-in templates are authored source data and must be reconciled
        // when their canonical mechanics change. A user-created monster that
        // happens to reuse a built-in id remains untouched.
        // rule_reference: AGENTS.md single source of truth; date_of_change: 2026-09-02
        return Array.isArray(existing?.metadata?.tags)
          && existing.metadata.tags.includes('template');
      });

      if (monsterIdsToReconcile.length === 0) {
        this.debugLog(`[DataMigration] No missing or built-in monster templates require reconciliation (${Object.keys(existingMonsters).length} present)`);
        return existingMonsters;
      }

      this.debugLog(`[DataMigration] Reconciling ${monsterIdsToReconcile.length} canonical monster templates...`);

      for (const monsterId of monsterIdsToReconcile) {
        const srd = monsterData[monsterId];
        try {
          this.debugLog(`[DataMigration] Processing monster: ${monsterId}`);
          this.debugLog(`[DataMigration] Loaded ${monsterId}:`, !!srd, !!srd?.data);

          if (srd && (srd.data || srd.name)) {
            const now = new Date().toISOString();
            const { id, data, metadata } = buildMonsterEntityFromRaw(srd, {
              now,
              defaultId: monsterId,
              markAsTemplate: true,
              markAsUpload: false
            });

            let entity = {
              schemaVersion: UnifiedDataManager.schemaVersions.monster,
              type: 'monster',
              id,
              data,
              metadata
            };

            // Three-way, versioned migration preserves edits to built-ins.
            // Never overwrite a saved template on every startup.
            // date_of_change: 2026-09-05
            const existing = existingMonsters[monsterId];
            if (existing) {
              const migration = migrateDefaultCreatureData(existing.data, monsterId);
              if (migration.status === 'blocked') {
                NotificationManager.warning('Monster migration needs review', `${monsterId}: ${migration.conflicts.join('; ')} See Character_Monster_Data_Architecture.md.`);
                continue;
              }
              if (!migration.changed) continue;
              entity = { ...existing, data: migration.data };
            }

            this.debugLog(`[DataMigration] Saving ${monsterId} to UnifiedDataManager (id=${entity.id})...`);
            const savedEntity = await UnifiedDataManager.saveEntity(entity);
            migratedMonsters[savedEntity.id] = savedEntity;
            this.debugLog(`[DataMigration] Migrated monster: ${data.name}`);
          } else {
            console.warn(`[DataMigration] Invalid entity structure for ${monsterId}:`, srd);
          }
        } catch (error) {
          console.warn(`[DataMigration] Failed to load monster ${monsterId}:`, error);
        }
      }

      this.debugLog(`[DataMigration] Monster migration complete: ${Object.keys(migratedMonsters).length} monsters available`);
      return migratedMonsters;

    } catch (error) {
      console.error('[DataMigration] Monster migration failed:', error);
      return {};
    }
  }

  /**
   * Migrate preloaded characters to UnifiedDataManager
   * date_of_change: 2025-08-29
   */
  static async migratePreloadedCharacters() {
    this.debugLog('[DataMigration] Starting character migration...');
    
    try {
      // Always reconcile the 7 built-in templates to canonical JSON
      this.debugLog('[DataMigration] Checking for existing characters...');
      const existingCharacters = UnifiedDataManager.getAllFromLocalStorage('character');
      const templateIds = ['fighter','wizard','rogue','cleric','ranger','paladin','barbarian'];
      const migratedCharacters = { ...existingCharacters };

      // @date_of_change: 2025-10-18 - rule_reference: AGENTS.md SSOT artwork governance
      // Normalize canonical artwork hints so Dashboard/TokenAdapter resolve to real assets instead of placeholders.
      const normalizeArtworkHint = (value) => {
        if (!value) return null;
        const trimmed = String(value).trim();
        if (!trimmed) return null;
        return trimmed;
      };
      const isAbsoluteAsset = (value) => /^https?:\/\//i.test(value || '') || /^data:/i.test(value || '');
      const stripToAssetPath = (value) => {
        if (!value) return null;
        if (!isAbsoluteAsset(value)) {
          return value.replace(/^\/+/, '');
        }
        try {
          const url = new URL(value);
          return url.pathname.replace(/^\/+/, '');
        } catch (_) {
          return value;
        }
      };
      const isPlaceholderArt = (value) => {
        if (!value) return true;
        const lower = value.toLowerCase();
        if (lower.includes('placeholder')) return true;
        if (lower.includes('token-default')) return true;
        if (/unified-[a-z0-9_-]+/i.test(lower)) return true;
        return false;
      };

      for (const templateId of templateIds) {
        try {
          this.debugLog(`[DataMigration] Processing character template: ${templateId}`);
          const src = CharacterDataService.getCharacterData(templateId);
          if (!src) {
            console.warn(`[DataMigration] Missing template data for ${templateId}, skipping`);
            continue;
          }

          // Build canonical schema-compliant character (data.*) from template
          const base = src.data || src;
          const compatOriginalData = base?.compat?.originalData || {};
          const toLongAbilities = (a) => {
            if (!a || typeof a !== 'object') return { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 };
            if (a.strength != null) return {
              strength: Number(a.strength), dexterity: Number(a.dexterity), constitution: Number(a.constitution),
              intelligence: Number(a.intelligence), wisdom: Number(a.wisdom), charisma: Number(a.charisma)
            };
            if (a.str != null) return {
              strength: Number(a.str), dexterity: Number(a.dex), constitution: Number(a.con),
              intelligence: Number(a.int), wisdom: Number(a.wis), charisma: Number(a.cha)
            };
            return { strength: 10, dexterity: 10, constitution: 10, intelligence: 10, wisdom: 10, charisma: 10 };
          };
          const resolveClasses = () => {
            const clsArr = base?.compat?.originalData?.classes;
            if (Array.isArray(clsArr) && clsArr.length) {
              return clsArr.map(c => ({
                name: String(c.name || base.class || 'Fighter'),
                level: Number(c.level || base.level || 1),
                subclass: c.subclass || '',
                hitDice: c.hitDice || 'd10'
              }));
            }
            if (base.class || base.level) {
              return [{ name: String(base.class || 'Fighter'), level: Number(base.level || 1), subclass: '', hitDice: 'd10' }];
            }
            return [{ name: 'Fighter', level: 1, subclass: '', hitDice: 'd10' }];
          };
          const resolveSkills = () => {
            const normalizeEnum = (val) => {
              // date_of_change: 2025-11-30 - Map legacy values to schema enums (expertise -> expert, half -> half-proficient)
              if (typeof val !== 'string') return val;
              const lower = val.toLowerCase();
              if (lower === 'expertise' || lower === 'expert') return 'expert';
              if (lower === 'half' || lower === 'half-proficient' || lower === 'half_proficient') return 'half-proficient';
              if (lower === 'proficient') return 'proficient';
              if (lower === 'none' || lower === 'untrained') return 'none';
              return 'none';
            };

            const compatSkills = base?.compat?.originalData?.skills;
            if (compatSkills && typeof compatSkills === 'object') {
              const out = {};
              for (const [k, v] of Object.entries(compatSkills)) {
                out[k] = normalizeEnum(v);
              }
              return out;
            }
            // Convert boolean/object profs to enum strings
            const out = {};
            if (base.skills && typeof base.skills === 'object') {
              for (const [k, v] of Object.entries(base.skills)) {
                if (v && typeof v === 'object') {
                  out[k] = v.expertise ? 'expert' : (v.proficient ? 'proficient' : 'none');
                } else if (v === true) {
                  out[k] = 'proficient';
                } else if (typeof v === 'string') {
                  out[k] = normalizeEnum(v);
                } else {
                  out[k] = 'none';
                }
              }
              return out;
            }
            return undefined;
          };
          const normalizeFeatureEntries = (entries) => (
            Array.isArray(entries)
              ? entries
                .filter(entry => entry && typeof entry === 'object')
                .map(entry => ({
                  ...entry,
                  description: entry.description || entry.text || entry.desc || ''
                }))
              : undefined
          );
          const collectSpellNames = (spellContainer) => {
            if (!spellContainer || typeof spellContainer !== 'object') return [];

            const collected = [];
            const pushSpellName = (spell) => {
              if (!spell) return;
              if (typeof spell === 'string') {
                collected.push(spell);
                return;
              }
              if (typeof spell === 'object' && spell.name) {
                collected.push(spell.name);
              }
            };

            ['prepared', 'known', 'spells', 'atWill', 'cantrips'].forEach((field) => {
              (spellContainer[field] || []).forEach(pushSpellName);
            });

            if (spellContainer.byLevel && typeof spellContainer.byLevel === 'object') {
              Object.values(spellContainer.byLevel).forEach((spellList) => {
                (Array.isArray(spellList) ? spellList : []).forEach(pushSpellName);
              });
            }

            for (let level = 0; level <= 9; level += 1) {
              const levelKey = level === 0 ? 'level0' : `level${level}`;
              (spellContainer[levelKey] || []).forEach(pushSpellName);
            }

            return Array.from(new Set(collected.filter(Boolean)));
          };
          const normalizeSpellReferenceList = (value) => (
            Array.isArray(value)
              ? value
                .map((spell) => (typeof spell === 'string' ? spell : spell?.name))
                .filter(Boolean)
              : undefined
          );
          const normalizeSpellSlots = (slots) => {
            if (!slots || typeof slots !== 'object') return undefined;

            const normalizedEntries = Object.entries(slots).reduce((acc, [level, slotData]) => {
              if (!slotData || typeof slotData !== 'object') return acc;

              const total = Number(slotData.total);
              const used = Number(slotData.used);
              if (!Number.isFinite(total) && !Number.isFinite(used)) {
                return acc;
              }

              acc[level] = {
                total: Number.isFinite(total) ? total : 0,
                used: Number.isFinite(used) ? used : 0
              };
              return acc;
            }, {});

            return Object.keys(normalizedEntries).length ? normalizedEntries : undefined;
          };
          const sanitizeSpellcastingPayload = (payload) => {
            if (!payload || typeof payload !== 'object') {
              return undefined;
            }

            const prepared = normalizeSpellReferenceList(payload.prepared);
            const known = normalizeSpellReferenceList(payload.known);
            const spellList = normalizeSpellReferenceList(payload.spellList)
              || collectSpellNames(payload);
            const slots = normalizeSpellSlots(payload.slots);
            const concentration = typeof payload.concentration === 'string' && payload.concentration.trim().length > 0
              ? payload.concentration
              : undefined;

            return {
              ...payload,
              ability: typeof payload.ability === 'string' ? payload.ability : undefined,
              spellList: spellList?.length ? spellList : undefined,
              slots,
              prepared,
              known,
              concentration
            };
          };
          const normalizeSpellcasting = () => {
            if (base.spellcasting && typeof base.spellcasting === 'object') {
              return sanitizeSpellcastingPayload(base.spellcasting);
            }
            if (compatOriginalData.spellcasting && typeof compatOriginalData.spellcasting === 'object') {
              return sanitizeSpellcastingPayload(compatOriginalData.spellcasting);
            }
            if (base.spells && typeof base.spells === 'object') {
              return sanitizeSpellcastingPayload({
                ability: base.spells.ability || undefined,
                spellList: collectSpellNames(base.spells),
                slots: base.spells.slots || undefined,
                prepared: (base.spells.prepared || []).map((spell) => (typeof spell === 'string' ? spell : spell?.name)).filter(Boolean)
              });
            }
            return undefined;
          };
          const normalizeEquipment = () => {
            const rawEquipment = base.equipment || compatOriginalData.equipment;
            if (!rawEquipment || typeof rawEquipment !== 'object') {
              return undefined;
            }

            const armor = Array.isArray(rawEquipment.armor) ? rawEquipment.armor : [];
            const weapons = Array.isArray(rawEquipment.weapons) ? rawEquipment.weapons : [];
            const items = [
              ...(Array.isArray(rawEquipment.items) ? rawEquipment.items : []),
              ...(Array.isArray(rawEquipment.other) ? rawEquipment.other : [])
            ];

            return {
              ...rawEquipment,
              armor,
              weapons,
              items
            };
          };
          // Built-in templates are trusted authored content. Preserve their explicit
          // action-economy object as canonical data instead of dropping it when the
          // source is not a legacy flat action array. date_of_change: 2026-08-11
          const normalizeActionEconomy = () => {
            const sourceActions = base.actions && typeof base.actions === 'object' && !Array.isArray(base.actions)
              ? base.actions
              : {};
            const actions = Array.isArray(base.actions)
              ? base.actions
              : (Array.isArray(sourceActions.actions) ? sourceActions.actions : []);
            const bonusActions = Array.isArray(base.bonusActions)
              ? base.bonusActions
              : (Array.isArray(sourceActions.bonusActions) ? sourceActions.bonusActions : []);
            const reactions = Array.isArray(base.reactions)
              ? base.reactions
              : (Array.isArray(sourceActions.reactions) ? sourceActions.reactions : []);

            if (actions.length === 0 && bonusActions.length === 0 && reactions.length === 0) {
              return undefined;
            }

            return {
              ...sourceActions,
              actions,
              bonusActions,
              reactions
            };
          };
          const canonicalData = {
            name: base.identity?.name || base.name || templateId,
            background: base.background,
            race: base.identity?.race || base.race,
            // date_of_change: 2025-11-30 - Ensure subrace is a string if present; schema disallows null
            subrace: (typeof base.subrace === 'string' && base.subrace.trim().length > 0) ? base.subrace : undefined,
            alignment: base.identity?.alignment || base.alignment,
            abilities: toLongAbilities(base.abilities),
            classes: resolveClasses(),
            skills: resolveSkills(),
            hitPoints: (base.hp != null || base.maxHp != null) ? {
              current: Number(base.hp != null ? base.hp : (base.hitPoints?.current ?? 0)),
              maximum: Number(base.maxHp != null ? base.maxHp : (base.hitPoints?.maximum ?? base.hp ?? 0)),
              temporary: Number(base.hitPoints?.temporary ?? 0)
            } : undefined,
            combatStats: base.combatStats || compatOriginalData.combatStats || undefined,
            savingThrows: base.savingThrows || compatOriginalData.savingThrows || undefined,
            spellcasting: normalizeSpellcasting(),
            spells: base.spells || compatOriginalData.spells || undefined,
            equipment: normalizeEquipment(),
            inventory: base.inventory || undefined,
            features: normalizeFeatureEntries(base.features) || normalizeFeatureEntries(compatOriginalData.features),
            classFeatures: normalizeFeatureEntries(base.classFeatures),
            speciesTraits: normalizeFeatureEntries(base.speciesTraits),
            feats: normalizeFeatureEntries(base.feats),
            actions: normalizeActionEconomy(),
            proficiencies: base.proficiencies || undefined,
            toolProficiencies: Array.isArray(base.toolProficiencies) ? base.toolProficiencies : undefined,
            languages: Array.isArray(base.languages) ? base.languages : undefined,
            biography: base.biography || undefined,
            appearance: base.appearance || undefined,
            relationships: Array.isArray(base.relationships) ? base.relationships : undefined,
            storage: base.storage || undefined,
            political: base.political || undefined,
            notes: base.notes || undefined,
            items: Array.isArray(base.items) ? base.items : undefined,
            conditions: Array.isArray(base.conditions) ? base.conditions : undefined,
            compat: base.compat || undefined,
            imageUrl: base.imageUrl || undefined
          };
          const now = new Date().toISOString();

          const existingMeta = existingCharacters[templateId]?._meta || {};
          const templateMeta = typeof CharacterDataService.getCharacterTemplate === 'function'
            ? CharacterDataService.getCharacterTemplate(templateId)
            : null;

          const artworkCandidates = [
            existingMeta.artwork,
            existingMeta.tokenAssetKey,
            base.imageUrl,
            base.compat?.originalData?.imageUrl,
            templateMeta?.imageUrl
          ];
          const canonicalArtwork = artworkCandidates
            .map(candidate => normalizeArtworkHint(candidate))
            .filter(candidate => candidate && !isPlaceholderArt(candidate))
            .shift() || null;

          const normalizedTokenAssetKey = (() => {
            const metaKey = normalizeArtworkHint(existingMeta.tokenAssetKey);
            if (metaKey && !isPlaceholderArt(metaKey)) {
              return stripToAssetPath(metaKey);
            }
            if (!canonicalArtwork) return null;
            const assetPath = stripToAssetPath(canonicalArtwork);
            if (!assetPath || isPlaceholderArt(assetPath)) return null;
            return assetPath;
          })();

          const entity = {
            schemaVersion: UnifiedDataManager.schemaVersions.character,
            type: 'character',
            exportDate: now,
            id: templateId,
            source: {
              name: 'Virtual Combat Simulator Templates',
              edition: '5e',
              license: 'Custom'
            },
            data: canonicalData,
            tags: ['template'],
            _meta: {
              createdAt: existingCharacters[templateId]?._meta?.createdAt || now,
              lastModified: now,
              artwork: canonicalArtwork || base.artwork || null,
              tokenAssetKey: normalizedTokenAssetKey || (base.artwork && String(base.artwork).startsWith('/uploads/') ? String(base.artwork).replace(/^\//,'') : null),
              tokenAsset: normalizedTokenAssetKey ? { key: normalizedTokenAssetKey } : existingMeta.tokenAsset || undefined
            }
          };

          const savedEntity = await UnifiedDataManager.saveEntity(entity);
          migratedCharacters[savedEntity.id] = savedEntity;
          this.debugLog(`[DataMigration] Saved canonical character: ${canonicalData.name || templateId}`);
        } catch (error) {
          console.error(`[DataMigration] Failed to migrate character ${templateId}:`, error);
        }
      }

      this.debugLog(`[DataMigration] Character migration complete: ${Object.keys(migratedCharacters).length} characters present`);
      return migratedCharacters;

    } catch (error) {
      console.error('[DataMigration] Character migration failed:', error);
      return {};
    }
  }

  /**
   * Run complete data migration
   * date_of_change: 2025-08-29
   */
  static async migrateAllData() {
    this.debugLog('[DataMigration] Starting complete data migration...');
    
    const [monsters, characters] = await Promise.all([
      this.migratePreloadedMonsters(),
      this.migratePreloadedCharacters()
    ]);

    this.debugLog('[DataMigration] Migration complete:', {
      monsterCount: Object.keys(monsters).length,
      characterCount: Object.keys(characters).length
    });

    return { monsters, characters };
  }
}

export default DataMigration;
